(() => {
  const APO_ENDPOINT_ORIGIN = 'https://apo-admin.mageworx.com';
  const PREPARE_DELAY = 180;
  const PREPARE_RETRY_DELAYS = [350, 900, 1800];
  const BACKGROUND_RETRY_DELAYS = [1500, 3000, 6000, 10000, 15000, 30000];
  const MAX_BACKGROUND_RETRIES = 2;
  const TOTAL_PREPARATION_TIMEOUT = 45000;
  const PREPARE_TIMEOUT = 30000;
  const READINESS_RETRY_DELAYS = [0, 400, 900, 1600, 2800, 4500, 6500, 8000];
  const READINESS_TIMEOUT = 30000;
  const CHECKOUT_TIMEOUT = 30000;
  const PRICE_QUOTE_MAX_AGE = 5 * 60 * 1000;
  const STOREFRONT_API_VERSION = '2026-07';
  const DEBUG = new URLSearchParams(window.location.search).has('joa_apo_debug');

  // Each APO form owns its verified option state; cart editors must not replace the PDP state.
  const apoFormStates = new WeakMap();
  // APO may reuse and mutate its values map between inline configurators.
  // Keep each form's published choices as a snapshot, not a shared reference.
  const snapshotApoState = (state) => state && ({
    ...state,
    values: Object.fromEntries(Object.entries(state.values || {}).map(([key, value]) => [key, { ...value }]))
  });

  const bridge = {
    apoActive: false,
    commonData: null,
    loadedState: null,
    apoForm: null,
    form: null,
    isolationObserver: null,
    walletObserver: null,
    preparedResponse: null,
    preparedSelectionFingerprint: null,
    timer: null,
    controlTimer: null,
    abortController: null,
    readinessAbortController: null,
    readinessTimer: null,
    readinessGeneration: 0,
    verifiedResponseFingerprint: null,
    verifiedSelectionFingerprint: null,
    verifiedPreparationFingerprint: null,
    apoSourcePrice: null,
    priceQuote: null,
    statusTimer: null,
    statusStartedAt: 0,
    checkoutAbortController: null,
    checkoutGeneration: 0,
    requestVersion: 0,
    backgroundRetryAttempt: 0,
    preparationDeadlineTimer: null,
    preparationFingerprint: null,
    exhaustedSelectionFingerprint: null,
    preparationSuspended: false,
    preparationRecoveryAllowed: false,
    isolatedCheckoutPending: false,
    expressAction: null,
    formdataForms: new WeakSet(),
    isolatedButtons: new WeakSet(),
    activationObserver: null,
    nativeReleaseTimer: null,
    apoNoOptionsTimer: null,
    apoDecisionKnown: false,
    apoAssigned: null,
    lockedApoContainer: null,
    cartConfigurationBusy: false,
    cartConfigurationControls: [],
    cartConfigurationUnlockTimer: null
  };

  document.documentElement.classList.add('joa-express-booting');

  /*
   * APO's optional Buy Now clone adds the configured item to the existing Ajax
   * cart before opening checkout. That is incompatible with a product-level
   * accelerated checkout because an existing cart is then included. Keep APO
   * responsible for configuration and pricing, but never expose its clone.
   */
  const apoBuyNowGuard = document.createElement('style');
  apoBuyNowGuard.textContent = `
    .mw-apo-buy-it-now-cloned {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
    html.joa-express-booting .shopify-product-form .shopify-payment-button {
      pointer-events: none !important;
    }
    html.joa-express-active .joa-product-form .accelerated-checkout-block,
    html.joa-express-active .joa-product-form .shopify-payment-button,
    html.joa-express-active .joa-product-form shopify-accelerated-checkout {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
    .joa-express-form:not([data-joa-express-state="ready"]) .shopify-payment-button,
    .joa-express-form[data-joa-wallet-state="resolving"] .shopify-payment-button {
      pointer-events: none !important;
    }
    .joa-express-form.joa-isolated-fallback .shopify-payment-button {
      display: none !important;
    }
    .joa-isolated-checkout-button {
      display: block;
      width: 100%;
      min-height: 55px;
      height: 55px;
      padding: 0 24px;
      border: 0;
      border-radius: var(--joa-control-radius, 4px);
      background: #fff;
      color: #000;
      font: inherit;
      line-height: 1;
      text-align: center;
      cursor: pointer;
    }
    .joa-isolated-checkout-button[disabled] {
      cursor: wait;
      opacity: .72;
    }
  `;
  document.head.appendChild(apoBuyNowGuard);

  const debug = (...args) => {
    if (DEBUG && window.console) console.info('[JOA express]', ...args);
  };

  const notifyApoStateChange = () => {
    document.dispatchEvent(new CustomEvent('joa:apo-statechange'));
  };

  const releaseNativeCheckout = () => {
    if (
      bridge.apoActive ||
      !bridge.apoDecisionKnown ||
      bridge.apoAssigned !== false
    ) {
      return;
    }

    window.clearTimeout(bridge.nativeReleaseTimer);
    bridge.nativeReleaseTimer = null;
    window.clearTimeout(bridge.apoNoOptionsTimer);
    bridge.apoNoOptionsTimer = null;
    bridge.activationObserver?.disconnect();
    bridge.activationObserver = null;
    document.documentElement.classList.remove('joa-express-booting');
    document.documentElement.classList.remove('joa-express-active');
    notifyApoStateChange();
  };

  const markApoAssigned = (reason) => {
    bridge.apoDecisionKnown = true;
    bridge.apoAssigned = true;
    window.clearTimeout(bridge.apoNoOptionsTimer);
    bridge.apoNoOptionsTimer = null;
    debug('APO assigned', reason);
  };

  const markApoNotApplicable = (reason) => {
    if (bridge.apoActive || bridge.apoAssigned === true) return;
    bridge.apoDecisionKnown = true;
    bridge.apoAssigned = false;
    debug('APO not assigned', reason);
    releaseNativeCheckout();
  };

  const scheduleApoNotApplicable = (reason) => {
    if (bridge.apoActive || bridge.apoAssigned === true) return;
    window.clearTimeout(bridge.apoNoOptionsTimer);
    bridge.apoNoOptionsTimer = window.setTimeout(() => {
      bridge.apoNoOptionsTimer = null;
      if (activateApoIfPresent()) {
        window.setTimeout(schedulePreparation, 0);
        return;
      }
      if (!bridge.commonData) markApoNotApplicable(reason);
    }, 1500);
  };

  const hasApoOptionControls = (container) =>
    Boolean(container?.querySelector('[name^="mw_opts["]'));

  const findApoOptionContainer = () => {
    const containers = Array.from(document.querySelectorAll('.mw-apo-container'))
      .filter(
        (container) =>
          hasApoOptionControls(container) &&
          !container.closest('.joa-express-form')
      );

    return (
      containers.find((container) => container.closest('product-form-component')) ||
      containers[0] ||
      null
    );
  };

  const markApoActive = (apoForm) => {
    if (!apoForm) return false;

    markApoAssigned('option controls rendered');
    bridge.apoActive = true;
    bridge.apoForm = apoForm;
    apoForm.classList.add('joa-product-form');
    window.clearTimeout(bridge.nativeReleaseTimer);
    bridge.nativeReleaseTimer = null;
    bridge.activationObserver?.disconnect();
    bridge.activationObserver = null;
    document.documentElement.classList.remove('joa-express-booting');
    document.documentElement.classList.add('joa-express-active');
    notifyApoStateChange();
    neutralizeExpressForm();
    return true;
  };

  const activateApoIfPresent = () => {
    const container = findApoOptionContainer();
    if (!container) return false;
    const apoForm = container.closest('form');
    if (!apoForm) return false;
    return markApoActive(apoForm);
  };

  const flattenPropertyValue = (value) => {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'string') return value;
    throw new Error('APO returned an unsupported property value');
  };

  const propertyEntries = (properties) => {
    if (Array.isArray(properties)) {
      return properties
        .filter((entry) => entry && (entry.key != null || entry.name != null))
        .map((entry) => [
          String(entry.key ?? entry.name),
          flattenPropertyValue(entry.value)
        ]);
    }

    if (!properties || typeof properties !== 'object') return [];

    return Object.entries(properties).map(([key, value]) => [
      key,
      flattenPropertyValue(value)
    ]);
  };

  const pageProductId = () => {
    const id = String(
      bridge.apoForm
        ?.closest('product-form-component[data-product-id]')
        ?.getAttribute('data-product-id') ||
      document.querySelector('product-form-component[data-product-id]')
        ?.getAttribute('data-product-id') ||
      ''
    );
    if (!/^[1-9]\d+$/.test(id)) {
      throw new Error('The product page identity is unavailable');
    }
    return id;
  };

  const pageProductHandle = () => {
    const match = window.location.pathname.match(/\/products\/([^/?]+)/);
    const handle = decodeURIComponent(match?.[1] || '');
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle)) {
      throw new Error('The product page handle is unavailable');
    }
    return handle;
  };

  const currencyMinorScale = (currency) => {
    try {
      const digits = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).resolvedOptions().maximumFractionDigits;
      return 10 ** Math.min(3, Math.max(2, digits));
    } catch (_error) {
      return 100;
    }
  };

  const minorAmountsMatch = (left, right) =>
    Number.isSafeInteger(left) &&
    Number.isSafeInteger(right) &&
    Math.abs(left - right) <= 1;

  const escapeRegExp = (value) =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const displayedProductPriceCents = (presentmentCurrency) => {
    const productId = pageProductId();
    const priceRoot = document.querySelector(
      `product-price[data-product-id="${productId}"]`
    );
    const priceElement =
      priceRoot?.querySelector(
        '.price__sale:not(.price__hidden) .price-item--sale'
      ) ||
      priceRoot?.querySelector('.price__regular .price') ||
      priceRoot?.querySelector('.price');
    const priceText = String(priceElement?.textContent || '');
    if (!priceText.toUpperCase().includes(presentmentCurrency)) {
      throw new Error('The configured product currency is unavailable');
    }
    if (/[-−()]/.test(priceText)) {
      throw new Error('The configured product price is invalid');
    }

    const matches = priceText
      .replace(/[\u00a0\u202f]/g, ' ')
      .replace(/’/g, "'")
      .match(/[-−]?\d[\d.,' ]*/g);
    if (!matches || matches.length !== 1) {
      throw new Error('The configured product price is unavailable');
    }

    const scale = currencyMinorScale(presentmentCurrency);
    const precision = Math.round(Math.log10(scale));
    const raw = matches[0].trim().replace(/ +/g, ' ');
    if (/^[-−]/.test(raw)) {
      throw new Error('The configured product price is invalid');
    }
    const lastDot = raw.lastIndexOf('.');
    const lastComma = raw.lastIndexOf(',');
    const lastSeparator = Math.max(lastDot, lastComma);
    const trailingDigits = lastSeparator >= 0 ? raw.slice(lastSeparator + 1) : '';
    const hasBothSeparators = lastDot >= 0 && lastComma >= 0;
    const hasDecimal = lastSeparator >= 0 && (
      hasBothSeparators ||
      (/^\d+$/.test(trailingDigits) && trailingDigits.length <= precision)
    );
    const integerPart = hasDecimal ? raw.slice(0, lastSeparator) : raw;
    const fraction = hasDecimal ? trailingDigits : '';
    if (hasDecimal && (!/^\d+$/.test(fraction) || fraction.length > precision)) {
      throw new Error('The configured product price is invalid');
    }

    let integerDigits = integerPart;
    if (!/^\d+$/.test(integerPart)) {
      const group = integerPart.match(/[.,' ]/)?.[0];
      const separator = escapeRegExp(group || '');
      const grouping = new RegExp(`^(?:\\d{1,3}(?:${separator}\\d{3})+|\\d{1,2}(?:${separator}\\d{2})+${separator}\\d{3})$`);
      if (!group || !grouping.test(integerPart)) {
        throw new Error('The configured product price is invalid');
      }
      integerDigits = integerPart.split(group).join('');
    }
    const minorUnits = Number(integerDigits) * scale + Number(fraction.padEnd(precision, '0'));
    if (!Number.isSafeInteger(minorUnits) || minorUnits < 0) {
      throw new Error('The configured product price is invalid');
    }
    return minorUnits;
  };

  const validateApoProperties = (properties) => {
    if (
      !properties ||
      Array.isArray(properties) ||
      Object.prototype.toString.call(properties) !== '[object Object]'
    ) {
      throw new Error('APO returned an unsupported property collection');
    }

    const entries = propertyEntries(properties);
    if (!entries.length || entries.length > 100) {
      throw new Error('APO returned an invalid property collection');
    }

    const seen = new Set();
    entries.forEach(([key, value]) => {
      if (
        !key ||
        key.length > 255 ||
        ['__proto__', 'prototype', 'constructor'].includes(key) ||
        seen.has(key) ||
        value.length > 5000
      ) {
        throw new Error('APO returned an invalid product property');
      }
      seen.add(key);
    });

    const publicEntries = entries.filter(([key]) => !key.startsWith('_'));
    if (!publicEntries.length) {
      throw new Error('APO returned no configured product options');
    }
    return { entries, publicEntries };
  };

  const normalizeResponseCurrency = (response, commonData, initConfig) => {
    const shopCurrency = String(initConfig?.shop_currency || '').toUpperCase();
    const cartCurrency = String(initConfig?.cart?.currency || '').toUpperCase();
    const presentmentCurrency = String(
      commonData?.multiCurrency?.currency || cartCurrency
    ).toUpperCase();

    if (
      !/^[A-Z]{3}$/.test(shopCurrency) ||
      !/^[A-Z]{3}$/.test(presentmentCurrency) ||
      (cartCurrency && cartCurrency !== presentmentCurrency)
    ) {
      throw new Error('Express checkout received an invalid currency');
    }

    /*
     * APO's CTA price token is defined in two-decimal money. Fail closed for
     * currencies requiring more precision instead of risking an incorrect
     * configured total. Shopify's normal Add to cart remains available.
     */
    if (
      currencyMinorScale(shopCurrency) !== 100 ||
      currencyMinorScale(presentmentCurrency) !== 100
    ) {
      throw new Error(
        'Express checkout is unavailable for this currency precision'
      );
    }

    const productId = pageProductId();
    if (String(initConfig?.product?.id || '') !== productId) {
      throw new Error('APO returned a different product');
    }

    const variantId = String(response?.variant_id ?? response?.variantId ?? '');
    if (!/^[1-9]\d+$/.test(variantId)) {
      throw new Error('APO returned an invalid product variant');
    }

    const { publicEntries } = validateApoProperties(response?.properties);
    const responseQuantity = Number(response?.qty ?? response?.quantity);
    if (
      responseQuantity !== 1 ||
      response.properties._mws_qty !== '1' ||
      typeof response.properties._mws_cart !== 'string' ||
      !/^\d+$/.test(response.properties._mws_cart) ||
      !Array.isArray(response.children) ||
      response.children.length !== 0
    ) {
      throw new Error('APO returned an unsupported cart structure');
    }

    const baseVariants = Array.isArray(initConfig?.product?.variants)
      ? initConfig.product.variants
      : [];
    const matchingBaseVariant = baseVariants.find(
      (candidate) => String(candidate?.id) === variantId
    );
    const basePresentmentCents = Number(matchingBaseVariant?.price);
    const rawCta = response.properties.__mws_apo_cta;

    if (typeof rawCta !== 'string') {
      if (response.properties._mws_required !== '') {
        throw new Error('APO returned an incomplete generated-price marker');
      }

      if (matchingBaseVariant) {
        const expectedHandle = String(
          initConfig?.product?.handle || pageProductHandle()
        );
        const displayedCents = displayedProductPriceCents(presentmentCurrency);
        const basePriceMatches = displayedCents === basePresentmentCents;
        // APO formats its converted amount before Shopify applies market
        // rounding. A base variant needs the same source-price proof as a
        // generated variant before the authoritative checkout quote can win.
        const requiresSourcePriceProof = !basePriceMatches &&
          shopCurrency !== presentmentCurrency && Boolean(currentSourcePrice());
        if (
          response.product_handle !== expectedHandle ||
          !Number.isSafeInteger(basePresentmentCents) ||
          basePresentmentCents < 0 ||
          (!basePriceMatches && !requiresSourcePriceProof)
        ) {
          throw new Error('The base product and checkout payload do not match');
        }
        if (requiresSourcePriceProof) debug('base price reconciliation requested', JSON.stringify({
          variantId, shopCurrency, presentmentCurrency, basePresentmentCents,
          displayedCents, sourceMinorUnits: currentSourcePrice()?.minorUnits
        }));
        return {
          ...response,
          __joaExpectedProductId: productId,
          __joaExpectedProductHandle: expectedHandle,
          __joaRequiresSourcePriceProof: requiresSourcePriceProof
        };
      }

      const expectedHandle = String(
        initConfig?.product?.handle || pageProductHandle()
      );
      const generatedHandleIsValid =
        response.product_handle === null ||
        response.product_handle === expectedHandle;

      if (
        !generatedHandleIsValid ||
        String(response.id ?? '') !== response.properties._mws_cart ||
        typeof response.token !== 'string' ||
        !/^[a-f0-9]{64}$/.test(response.token) ||
        displayedProductPriceCents(presentmentCurrency) <= 0
      ) {
        throw new Error('APO returned an invalid generated-price variant');
      }

      debug('generated variant validation', {
        productId,
        shopCurrency,
        presentmentCurrency,
        variantId
      });
      return {
        ...response,
        /*
         * For generated-price variants response.id is APO's internal cart
         * record, not a Shopify Product ID. The exact prepared variant,
         * quantity, attributes, subtotal, and checkout URL are verified later;
         * do not compare the Shopify line product to this unrelated APO ID.
         */
        __joaExpectedProductId: '',
        __joaExpectedProductHandle: ''
      };
    }

    if (
      !matchingBaseVariant ||
      !Number.isSafeInteger(basePresentmentCents) ||
      basePresentmentCents < 0
    ) {
      throw new Error('APO combined incompatible generated and CTA pricing');
    }

    let cta;
    try {
      cta = JSON.parse(rawCta);
    } catch (_error) {
      throw new Error('APO returned invalid configured-price JSON');
    }

    if (
      !cta ||
      Object.prototype.toString.call(cta) !== '[object Object]' ||
      typeof cta.price !== 'string' ||
      !Array.isArray(cta.attributes) ||
      !cta.attributes.every(
        (attribute) =>
          Object.prototype.toString.call(attribute) === '[object Object]' &&
          typeof attribute.key === 'string' &&
          typeof attribute.value === 'string'
      )
    ) {
      throw new Error('APO returned an invalid configured-price payload');
    }

    const publicPropertyMap = new Map(publicEntries);
    const attributeKeys = new Set();
    if (
      cta.attributes.length !== publicPropertyMap.size ||
      !cta.attributes.every((attribute) => {
        if (attributeKeys.has(attribute.key)) return false;
        attributeKeys.add(attribute.key);
        return publicPropertyMap.get(attribute.key) === attribute.value;
      })
    ) {
      throw new Error('APO returned incomplete configuration attributes');
    }

    let surchargePresentmentCents = 0;
    if (cta.price !== '__') {
      const match = cta.price.match(/^(0|[1-9]\d*)\.(\d{2})__$/);
      if (!match) {
        throw new Error('APO returned an unsupported configured-price format');
      }

      const surchargeShopCents = Number(match[1]) * 100 + Number(match[2]);
      if (!Number.isSafeInteger(surchargeShopCents) || surchargeShopCents < 0) {
        throw new Error('APO returned an invalid configured price');
      }

      const rate = shopCurrency === presentmentCurrency
        ? 1
        : Number(commonData?.multiCurrency?.rate);
      if (!Number.isFinite(rate) || rate <= 0 || rate > 1000000000) {
        throw new Error('APO did not provide a valid currency conversion rate');
      }
      if (
        shopCurrency !== presentmentCurrency &&
        (
          commonData?.multiCurrency?.enabled !== true ||
          commonData?.multiCurrency?.is_cta_mode !== true
        )
      ) {
        throw new Error('APO currency conversion is not enabled for CTA pricing');
      }

      const surchargePresentment = Number(
        ((surchargeShopCents / 100) * rate).toFixed(3)
      );
      surchargePresentmentCents = Math.round(
        surchargePresentment * currencyMinorScale(presentmentCurrency)
      );
      if (shopCurrency !== presentmentCurrency) {
        cta.price = `${surchargePresentment.toFixed(2)}__`;
      }
    }

    const expectedTotalCents = basePresentmentCents + surchargePresentmentCents;
    const displayedTotalCents = displayedProductPriceCents(presentmentCurrency);
    if (!minorAmountsMatch(expectedTotalCents, displayedTotalCents)) {
      throw new Error('The configured page and checkout prices do not match');
    }

    debug('price validation', {
      productId,
      shopCurrency,
      presentmentCurrency,
      basePresentmentCents,
      surchargePresentmentCents,
      expectedTotalCents
    });

    return {
      ...response,
      __joaExpectedProductId: productId,
      __joaExpectedProductHandle: pageProductHandle(),
      properties: {
        ...response.properties,
        __mws_apo_cta: JSON.stringify(cta)
      }
    };
  };

  const stripSellingPlans = (form) => {
    const sellingPlans = Array.from(
      form?.querySelectorAll('[name="selling_plan"]') || []
    );
    sellingPlans.forEach((input) => input.remove());
    return sellingPlans.length > 0;
  };

  const selectedSellingPlanIsActive = (form) =>
    Array.from(form?.querySelectorAll('[name="selling_plan"]') || []).some(
      (input) =>
        !input.disabled &&
        String(input.value || '').trim() !== '' &&
        (input.type === 'hidden' || input.checked)
    );

  const isDepositSelected = () => {
    const depositRadio = document.querySelector(
      '#purchase-option-deposit-input'
    );
    const fullPaymentRadio = document.querySelector('#purchase-option-full');

    /*
     * Downpay leaves a populated selling_plan field in the product form while
     * "Pay in Full" is selected. Once its purchase-option controls exist, the
     * checked radio is the authoritative state. Treating the hidden field as a
     * deposit made the express form wait forever and CSS consequently hid every
     * product-page accelerated button.
     */
    if (depositRadio || fullPaymentRadio) {
      return depositRadio?.checked === true;
    }

    return (
      document.body?.classList.contains('downpay-deposit-selected') === true ||
      selectedSellingPlanIsActive(bridge.apoForm)
    );
  };

  const productCanCheckout = (form) =>
    form?.dataset.joaCanCheckout === 'true' &&
    !form.querySelector('.accelerated-checkout-block[hidden]');

  const localizedCartAddAction = () => {
    const root = String(window.Shopify?.routes?.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart/add`.replace(/\/+/g, '/');
  };

  const preserveStandardExpressFields = (form) => {
    if (!form) return;

    const standardValues = {
      form_type: 'product',
      utf8: '✓'
    };

    Object.entries(standardValues).forEach(([name, value]) => {
      const input = form.querySelector(`input[name="${name}"]`);
      if (!input) return;
      if (input.getAttribute('data-joa-value') !== value) {
        input.setAttribute('data-joa-value', value);
      }
      if (input.value !== value) input.value = value;
      if (input.getAttribute('value') !== value) {
        input.setAttribute('value', value);
      }
    });

    if (bridge.formdataForms.has(form)) return;
    bridge.formdataForms.add(form);
    form.dataset.joaFormdataBound = 'true';

    form.addEventListener('formdata', (event) => {
      Object.entries(standardValues).forEach(([name, value]) => {
        event.formData.set(name, value);
      });

      /*
       * Remove every mutable prepared-line field first. Only a payload whose
       * live APO selection still matches is allowed back into Shopify's native
       * wallet submission. A late/programmatic option change therefore fails
       * closed instead of submitting the previous ring configuration.
       */
      Array.from(event.formData.keys()).forEach((key) => {
        if (
          key === 'id' ||
          key === 'quantity' ||
          key === 'selling_plan' ||
          /^properties\[/.test(key) ||
          /^shopify-additional-items\[/.test(key)
        ) {
          event.formData.delete(key);
        }
      });

      const response = bridge.preparedResponse;
      const selectionFingerprint = currentApoSelectionFingerprint(bridge.apoForm);
      if (
        form.dataset.joaExpressState === 'ready' &&
        !isDepositSelected() &&
        response &&
        selectionFingerprint === bridge.preparedSelectionFingerprint &&
        preparedPayloadIsVerified(form, response)
      ) {
        event.formData.set(
          'id',
          String(response.variant_id ?? response.variantId ?? '')
        );
        event.formData.set(
          'quantity',
          String(response.qty ?? response.quantity ?? 1)
        );
        propertyEntries(response.properties).forEach(([key, value]) => {
          event.formData.set(`properties[${key}]`, value);
        });
      }
    });
  };

  const neutralizeExpressForm = () => {
    const form = document.querySelector('.joa-express-form');
    if (!form) return null;

    const formChanged = bridge.form !== form;
    if (formChanged) {
      form.dataset.joaWalletState = 'resolving';
      const originalAction = form.getAttribute('action') || localizedCartAddAction();
      let parsedAction;
      try {
        parsedAction = new URL(originalAction, window.location.origin);
      } catch (_error) {
        parsedAction = null;
      }
      bridge.expressAction =
        parsedAction?.origin === window.location.origin &&
        /\/cart\/add\/?$/.test(parsedAction.pathname)
          ? parsedAction.pathname
          : localizedCartAddAction();
    }
    const expectedAction =
      bridge.expressAction || localizedCartAddAction();
    if (form.getAttribute('action') !== expectedAction) {
      form.setAttribute('action', expectedAction);
    }
    form.dataset.joaExpressIsolated = 'true';
    stripSellingPlans(form);
    preserveStandardExpressFields(form);
    bridge.form = form;

    if (formChanged) {
      bridge.isolationObserver?.disconnect();
      bridge.walletObserver?.disconnect();
      bridge.isolationObserver = null;
      bridge.walletObserver = null;
    }

    if (!bridge.isolationObserver) {
      bridge.isolationObserver = new MutationObserver(() => {
        const expectedAction =
          bridge.expressAction || localizedCartAddAction();
        if (form.getAttribute('action') !== expectedAction) {
          form.setAttribute('action', expectedAction);
        }
        if (stripSellingPlans(form)) schedulePreparation();
        pinPreparedPayload();
        scheduleControlSync(0);
      });
      bridge.isolationObserver.observe(form, {
        attributes: true,
        attributeFilter: ['action', 'value', 'variant-params', 'recommended'],
        childList: true,
        subtree: true
      });

      bridge.walletObserver = new MutationObserver(() => {
        scheduleControlSync(0);
      });
      bridge.walletObserver.observe(form, {
        attributes: true,
        attributeFilter: ['class', 'recommended', 'fallback'],
        childList: true,
        subtree: true
      });
    }

    return form;
  };

  const findForms = () => {
    const container = findApoOptionContainer();
    const apoForm = container ? container.closest('form') : null;
    const expressForm = neutralizeExpressForm();

    if (apoForm) {
      markApoActive(apoForm);
      bridge.apoForm = apoForm;
    }

    return { apoForm, expressForm };
  };

  const isVisible = (element) => {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    if (element.classList.contains('mw-option--hidden')) return false;
    return element.getClientRects().length > 0;
  };

  const apoSelectionStateMatches = (form, state = apoFormStates.get(form) || (form === bridge.apoForm ? bridge.loadedState : null)) => {
    if (!form || !state?.values) return true;
    return Array.from(form.querySelectorAll('[name^="mw_opts["]')).every((control) => {
      if (control.disabled || !isVisible(control.closest('.mw-option'))) return true;
      if (control.type === 'radio' || control.type === 'checkbox') {
        const value = state.values[control.value];
        return typeof value?.enabled !== 'boolean' || control.checked === value.enabled;
      }
      if (control.tagName === 'SELECT') {
        return Array.from(control.options).every((option) => {
          const value = state.values[option.value];
          return typeof value?.enabled !== 'boolean' || option.selected === value.enabled;
        });
      }
      return true;
    });
  };

  const requiredOptionsAreValid = (form) => {
    if (!apoSelectionStateMatches(form)) return false;
    const container = form.querySelector('.mw-apo-container');
    if (!container) return false;
    if (container.querySelector('input[type="file"]')) return false;

    const optionControls = Array.from(
      container.querySelectorAll('[name^="mw_opts["]')
    ).filter((control) => !control.disabled);
    if (!optionControls.length) return false;

    const requiredOptions = Array.from(
      container.querySelectorAll('.mw-option--required')
    ).filter(isVisible);

    if (!requiredOptions.length) return true;

    return requiredOptions.every((option) => {
      const controls = Array.from(
        option.querySelectorAll('[name^="mw_opts["]')
      ).filter((control) => !control.disabled);

      if (!controls.length) return false;

      const selectionControls = controls.filter((control) =>
        ['radio', 'checkbox'].includes(control.type)
      );
      if (selectionControls.length) {
        return selectionControls.some((control) => control.checked);
      }

      const select = controls.find((control) => control.tagName === 'SELECT');
      if (select) {
        const selected = select.options[select.selectedIndex];
        return Boolean(select.value && selected && !selected.disabled);
      }

      return controls.some((control) => String(control.value || '').trim() !== '');
    });
  };

  const engravingValue = (form) => form?.querySelector('joa-engraving [data-engraving-property]')?.value || '';

  const birthstoneSelection = (form = bridge.apoForm) => {
    const stones = form?.querySelector('joa-birthstones');
    const input = stones?.querySelector('[data-birthstone-property]:not(:disabled)');
    const ids = window.JoaBirthstones?.valid(input?.value || '[]') || [];
    if (!ids.length) return null;
    const variantId = String(stones.dataset.variantId || '');
    const total = ids.length * Number(stones.dataset.unitPrice);
    if (!/^[1-9]\d+$/.test(variantId) || !Number.isSafeInteger(total) || total <= 0) {
      throw new Error('The birthstone selection is incomplete');
    }
    return {
      included: stones.priceIncluded(), total: stones.extraTotal(), currency: stones.dataset.currency,
      properties: {
        Birthstones: stones.summary(),
        _joa_birthstone_ids: JSON.stringify(ids),
        ...(stones.priceIncluded() ? { _joa_birthstone_pricing: 'included' } : { _joa_birthstone_variant: variantId, _joa_birthstone_plans: stones.dataset.planIds || '' }),
        _joa_birthstone_labels: ids.map((key, index) => `${index + 1}. ${stones.copy[key]}`).join(' → ')
      }
    };
  };

  const currentApoSelectionFingerprint = (form) => {
    if (!form) return '';
    return JSON.stringify(
      Array.from(form.querySelectorAll('[name^="mw_opts["], [data-engraving-property], [data-personalization-property], [data-birthstone-property]'))
        .filter((control) => !control.disabled)
        .map((control, index) => [
          index,
          control.name,
          ['radio', 'checkbox'].includes(control.type)
            ? (control.checked ? String(control.value || '') : '')
            : String(control.value || '')
        ])
    );
  };

  const preparedResponseFingerprint = (response) => {
    if (!response) return '';
    return JSON.stringify({
      variantId: String(response.variant_id ?? response.variantId ?? ''),
      quantity: Number(response.qty ?? response.quantity),
      properties: propertyEntries(response.properties)
        .slice()
        .sort(([left], [right]) => left.localeCompare(right))
    });
  };

  const currentPriceContext = () => ({
    country: String(window.Shopify?.country ||
      bridge.form?.querySelector('shopify-accelerated-checkout')?.getAttribute('buyer-country') || '').toUpperCase(),
    currency: String(window.Shopify?.currency?.active ||
      bridge.commonData?.multiCurrency?.currency ||
      bridge.commonData?.initConfig?.cart?.currency || '').toUpperCase()
  });

  const customerQuantity = (form) =>
    form?.querySelector('input[name="quantity"]:not([type="hidden"]), select[name="quantity"]')?.value || '1';

  const currentSourcePrice = (form = bridge.apoForm) => {
    const source = bridge.apoSourcePrice;
    const init = bridge.commonData?.initConfig || window.mwApoInit;
    if (!source || init?.settings?.is_create_variant_cta !== false ||
      init?.shop_currency !== 'USD' ||
      source.selection !== currentApoSelectionFingerprint(form) ||
      customerQuantity(form) !== '1') return null;
    return source;
  };

  const currentPreparationFingerprint = (form) => JSON.stringify({
    selection: currentApoSelectionFingerprint(form),
    quantity: customerQuantity(form),
    ...currentPriceContext(),
    // The app's unconverted configuration price is independent of our display
    // update. Using rendered text here would restart verification on every fix.
    price: currentSourcePrice(form)?.minorUnits ??
      (document.querySelector(`product-price[data-product-id="${pageProductId()}"]`)
        ?.textContent?.replace(/\s+/g, ' ').trim() || '')
  });

  const applyVerifiedPrice = () => {
    const quote = bridge.priceQuote;
    if (!quote || Date.now() - quote.verifiedAt >= PRICE_QUOTE_MAX_AGE || !currentSourcePrice() ||
      quote.preparationFingerprint !== currentPreparationFingerprint(bridge.apoForm)) return;
    const roots = [...document.querySelectorAll(`product-price[data-product-id="${pageProductId()}"]`),
      ...document.querySelectorAll('sticky-add-to-cart .sticky-add-to-cart__price')];
    const fractionDigits = Math.round(Math.log10(currencyMinorScale(quote.subtotalCurrency)));
    const text = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: quote.subtotalCurrency, currencyDisplay: 'narrowSymbol', useGrouping: Math.abs(quote.subtotalMinorUnits / currencyMinorScale(quote.subtotalCurrency)) >= 10000,
      minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits
    }).format(quote.subtotalMinorUnits / currencyMinorScale(quote.subtotalCurrency)) + ` ${quote.subtotalCurrency}`;
    for (const root of roots) {
      const node = root.querySelector('.price__sale:not(.price__hidden) .price-item--sale') ||
        root.querySelector('.price__regular .price') || root.querySelector('.price') ||
        (root.matches('.sticky-add-to-cart__price') ? root : null);
      if (node && node.textContent !== text) node.textContent = text;
    }
    // Update full-payment and deposit labels in the same task, before repaint.
    document.dispatchEvent(new CustomEvent('joa:price-verified'));
  };

  const invalidateReadiness = () => {
    bridge.readinessGeneration += 1;
    bridge.readinessAbortController?.abort();
    bridge.readinessAbortController = null;
    window.clearTimeout(bridge.readinessTimer);
    bridge.readinessTimer = null;
    bridge.verifiedResponseFingerprint = null;
    bridge.verifiedSelectionFingerprint = null;
    bridge.verifiedPreparationFingerprint = null;
    bridge.priceQuote = null;
  };

  const preparedPayloadIsVerified = (form, response = bridge.preparedResponse) => {
    if (!form || !response) return false;
    const responseFingerprint = preparedResponseFingerprint(response);
    const selectionFingerprint = currentApoSelectionFingerprint(bridge.apoForm);
    return Boolean(
      responseFingerprint &&
      selectionFingerprint &&
      responseFingerprint === bridge.verifiedResponseFingerprint &&
      selectionFingerprint === bridge.preparedSelectionFingerprint &&
      selectionFingerprint === bridge.verifiedSelectionFingerprint &&
      currentPreparationFingerprint(bridge.apoForm) === bridge.verifiedPreparationFingerprint &&
      bridge.priceQuote && Date.now() - bridge.priceQuote.verifiedAt < PRICE_QUOTE_MAX_AGE
    );
  };

  const clearPreparedPayload = (form) => {
    invalidateReadiness();
    form?.querySelector('[data-joa-express-payload]')?.remove();
    form
      ?.querySelectorAll('input[name="id"], input[name="quantity"]')
      .forEach((input) => input.removeAttribute('data-joa-value'));
    bridge.preparedResponse = null;
    bridge.preparedSelectionFingerprint = null;
  };

  const busyExpressState = (state) =>
    state === 'preparing' || state === 'verifying';

  const updateExpressStatus = (form, state, previousState) => {
    const status = form.querySelector('[data-joa-express-status]');
    const text = status?.querySelector('[data-joa-express-status-text]');
    const busy = busyExpressState(state);
    const wasBusy = busyExpressState(previousState);

    form.setAttribute('aria-busy', String(busy));
    if (!status || !text) return;

    const unavailable = state === 'unavailable' && !isDepositSelected();
    const waiting = state === 'waiting' && !isDepositSelected() && productCanCheckout(form);
    status.hidden = !busy && !unavailable && !waiting;
    const retry = status.querySelector('[data-joa-express-retry]');
    if (retry) retry.hidden = !unavailable;
    const spinner = status.querySelector('.joa-express-status__spinner');
    if (spinner) spinner.style.display = busy ? '' : 'none';
    if (!busy) {
      window.clearTimeout(bridge.statusTimer);
      bridge.statusTimer = null;
      bridge.statusStartedAt = 0;
      if (unavailable) text.textContent = status.dataset.unavailableMessage || '';
      else if (waiting) text.textContent = status.dataset.waitingMessage || '';
      return;
    }

    if (!wasBusy || !bridge.statusStartedAt) {
      bridge.statusStartedAt = Date.now();
      window.clearTimeout(bridge.statusTimer);
      bridge.statusTimer = window.setTimeout(() => {
        if (
          bridge.form === form &&
          busyExpressState(form.dataset.joaExpressState)
        ) {
          text.textContent = 'This can take a few seconds…';
        }
        bridge.statusTimer = null;
      }, 7000);
    }

    if (!navigator.onLine) {
      text.textContent = 'Waiting for your connection…';
    } else if (Date.now() - bridge.statusStartedAt >= 7000) {
      text.textContent = 'This can take a few seconds…';
    } else {
      text.textContent = state === 'verifying'
        ? 'Confirming your selection…'
        : form.dataset.joaExpressHasBeenReady === 'true'
          ? 'Updating your selection…'
          : 'Preparing secure checkout…';
    }
  };

  const setFormState = (form, state) => {
    if (!form) return;
    if (state === 'ready' && !preparedPayloadIsVerified(form)) {
      state = 'verifying';
    }
    if (bridge.apoActive) form.dataset.joaBirthstonesSupported = 'true';
    const previousState = form.dataset.joaExpressState;
    form.dataset.joaExpressState = state;
    if (state === 'unavailable' && bridge.preparationFingerprint) {
      bridge.exhaustedSelectionFingerprint = bridge.preparationFingerprint;
    }
    if (!busyExpressState(state)) {
      window.clearTimeout(bridge.preparationDeadlineTimer);
      bridge.preparationDeadlineTimer = null;
    }
    const depositSelected = isDepositSelected();
    const shell = form.closest('.joa-express-shell');
    if (shell) {
      shell.dataset.joaExpressState = state;
      shell.dataset.joaDepositSelected = String(depositSelected);
    }
    const ready =
      state === 'ready' &&
      productCanCheckout(form) &&
      !depositSelected &&
      preparedPayloadIsVerified(form);
    const gate = form.querySelector('[data-joa-express-gate]');
    const paymentButton = form.querySelector('.shopify-payment-button');

    /*
     * Keep Shopify's wallet component renderable while APO prepares the line.
     * Interactions remain gated until the payload and current selections match.
     */
    form.inert = false;
    form.removeAttribute('inert');
    form.removeAttribute('aria-hidden');
    if (gate) {
      gate.disabled = !ready;
      gate.setAttribute('aria-disabled', String(!ready));
    }
    if (paymentButton) {
      paymentButton.inert = !ready;
      paymentButton.toggleAttribute('inert', !ready);
    }
    if (ready) {
      form.dataset.joaExpressHasBeenReady = 'true';
      bridge.preparationRecoveryAllowed = false;
    }
    updateExpressStatus(form, state, previousState);

    form.dataset.joaWalletState = ready ? 'native' : 'resolving';

    scheduleControlSync(0);
    debug('state', state);
  };

  const stopPendingRequest = () => {
    window.clearTimeout(bridge.timer);
    bridge.timer = null;
    bridge.abortController?.abort();
    bridge.abortController = null;
    invalidateReadiness();
    bridge.requestVersion += 1;
    bridge.backgroundRetryAttempt = 0;
    window.clearTimeout(bridge.preparationDeadlineTimer);
    bridge.preparationDeadlineTimer = null;
    bridge.preparationFingerprint = null;
    bridge.exhaustedSelectionFingerprint = null;
    bridge.preparationRecoveryAllowed = false;
  };

  const shopCartForPreparation = (cart) => ({
    items: Array.isArray(cart?.items)
      ? cart.items.map((item) => ({ properties: item?.properties || {} }))
      : [],
    item_count: Number(cart?.item_count || 0)
  });

  const clonePreparationState = (loadedState) => {
    // APO can initialize this optional field as null, but its cart endpoint
    // requires a property map even when no extra properties were selected.
    const state = { ...(loadedState || {}), extra_properties: loadedState?.extra_properties ?? {}, html: '' };
    delete state.assigned_sets;
    return state;
  };

  const addHiddenInput = (container, name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    input.setAttribute('value', input.value);
    input.setAttribute('data-joa-value', input.value);
    container.appendChild(input);
    return input;
  };

  const upsertAcceleratedVariantParams = (form, variantId) => {
    const acceleratedCheckout = form.querySelector(
      'shopify-accelerated-checkout[variant-params]'
    );
    if (!acceleratedCheckout) return true;

    try {
      const numericVariantId = Number(variantId);
      if (!Number.isSafeInteger(numericVariantId) || numericVariantId < 1) {
        return false;
      }
      const parsed = JSON.parse(
        acceleratedCheckout.getAttribute('variant-params') || '[]'
      );
      const params = Array.isArray(parsed) ? parsed : [parsed];
      const currentVariantId = form.querySelector('input[name="id"]')?.value;
      const shippingSource = params.find(
        (entry) => String(entry?.id || '') === String(currentVariantId || '')
      ) || params[0];
      const requiresShipping = shippingSource?.requiresShipping !== false;
      const existing = params.find(
        (entry) => Number(entry?.id) === numericVariantId
      );

      if (!existing) {
        params.push({
          id: numericVariantId,
          requiresShipping
        });
      }

      const nextParams = JSON.stringify(params);
      if (acceleratedCheckout.getAttribute('variant-params') !== nextParams) {
        acceleratedCheckout.setAttribute('variant-params', nextParams);
      }
      return true;
    } catch (_error) {
      return false;
    }
  };

  const installPreparedPayload = (form, response) => {
    const parentVariantId = response?.variant_id ?? response?.variantId;
    const parentQuantity = Number(response?.qty ?? response?.quantity);
    const parentProperties = propertyEntries(response?.properties);
    const token = response?.token;

    if (
      !parentVariantId ||
      !Number.isInteger(parentQuantity) ||
      parentQuantity < 1 ||
      typeof token !== 'string' ||
      !token.trim() ||
      !/^[a-f0-9]{64}$/.test(token)
    ) {
      throw new Error('APO did not return a complete parent line');
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) throw new Error('Product form is missing its variant input');

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (!quantityInput) throw new Error('Express form is missing its quantity input');

    const payload = document.createElement('div');
    payload.hidden = true;
    payload.dataset.joaExpressPayload = '';

    parentProperties.forEach(([key, value]) => {
      addHiddenInput(payload, `properties[${key}]`, value);
    });

    const children = Array.isArray(response.children) ? response.children : [];
    children.forEach((child, childIndex) => {
      const index = childIndex + 1;
      const childVariantId = child?.variant_id ?? child?.variantId ?? child?.id;
      const childQuantity = Number(child?.qty ?? child?.quantity);
      if (
        !childVariantId ||
        !Number.isInteger(childQuantity) ||
        childQuantity < 1
      ) {
        throw new Error('APO returned an incomplete child line');
      }

      addHiddenInput(payload, `shopify-additional-items[${index}][id]`, childVariantId);
      addHiddenInput(
        payload,
        `shopify-additional-items[${index}][quantity]`,
        childQuantity
      );

      const childProperties = propertyEntries(child?.properties).map(([key, value]) => ({
        key,
        value
      }));

      if (childProperties.length) {
        addHiddenInput(
          payload,
          `shopify-additional-items[${index}][properties]`,
          JSON.stringify(childProperties)
        );
      }

      const sellingPlan = child?.selling_plan ?? child?.sellingPlan;
      if (sellingPlan) {
        addHiddenInput(
          payload,
          `shopify-additional-items[${index}][selling_plan]`,
          sellingPlan
        );
      }
    });

    if (!upsertAcceleratedVariantParams(form, parentVariantId)) {
      throw new Error('Shopify returned invalid accelerated-checkout parameters');
    }

    clearPreparedPayload(form);
    form.appendChild(payload);
    preserveStandardExpressFields(form);
    quantityInput.value = String(parentQuantity);
    quantityInput.setAttribute('value', quantityInput.value);
    quantityInput.setAttribute('data-joa-value', quantityInput.value);
    variantInput.value = String(parentVariantId);
    variantInput.setAttribute('value', variantInput.value);
    variantInput.setAttribute('data-joa-value', variantInput.value);
    bridge.preparedResponse = response;
    bridge.preparedSelectionFingerprint =
      currentApoSelectionFingerprint(bridge.apoForm);

    document.cookie = [
      `_mws_apo_cart_token=${encodeURIComponent(token)}`,
      'Max-Age=1209600',
      'Path=/',
      'SameSite=None',
      'Secure'
    ].join('; ');

    debug('prepared', {
      parentVariantId: String(parentVariantId),
      parentPropertyCount: parentProperties.length,
      childCount: children.length
    });
  };

  const pinInputValue = (input, value) => {
    if (!input) return false;
    const nextValue = String(value);
    if (input.value !== nextValue) input.value = nextValue;
    if (input.getAttribute('value') !== nextValue) {
      input.setAttribute('value', nextValue);
    }
    return true;
  };

  /*
   * APO watches every product form and clears variant/property inputs that it
   * does not own. The express form is intentionally separate, so keep its
   * already-validated payload pinned without depending on a browser-specific
   * click target or event order.
   */
  const pinPreparedPayload = () => {
    const form = neutralizeExpressForm();
    const response = bridge.preparedResponse;
    if (
      !form ||
      form.dataset.joaExpressState !== 'ready' ||
      !preparedPayloadIsVerified(form, response)
    ) return false;

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const parentVariantId =
      response?.variant_id ??
      response?.variantId ??
      variantInput?.getAttribute('data-joa-value');
    const parentQuantity = Number(
      response?.qty ??
      response?.quantity ??
      quantityInput?.getAttribute('data-joa-value')
    );
    const payload = form.querySelector('[data-joa-express-payload]');

    if (!payload || !parentVariantId || !Number.isInteger(parentQuantity)) return false;

    pinInputValue(variantInput, parentVariantId);
    pinInputValue(quantityInput, parentQuantity);

    form.querySelectorAll('input[data-joa-value]').forEach((input) => {
      const expectedValue = input.getAttribute('data-joa-value');
      if (expectedValue != null) pinInputValue(input, expectedValue);
    });

    if (!upsertAcceleratedVariantParams(form, parentVariantId)) return false;

    return true;
  };

  const preparedLineAttributes = (response) =>
    propertyEntries(response?.properties).map(([key, value]) => ({ key, value }));

  const isolatedCheckoutMutation = `
    mutation IsolatedCheckout($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          cost {
            subtotalAmount { amount currencyCode }
          }
          lines(first: 10) {
            nodes {
              id
              quantity
              attributes { key value }
              cost { totalAmount { amount currencyCode } }
              ... on CartLine { parentRelationship { parent { id } } }
              merchandise {
                ... on ProductVariant {
                  id
                  product { id handle }
                }
              }
            }
          }
        }
        userErrors { field message code }
        warnings { code message target }
      }
    }
  `;

  const setApoConfigurationLocked = (locked) => {
    const unlock = (container) => {
      if (!container) return;
      container.removeAttribute('inert');
      container.removeAttribute('aria-busy');
    };

    if (!locked && !bridge.cartConfigurationBusy) {
      const currentContainer = findApoOptionContainer();
      unlock(bridge.lockedApoContainer);
      if (currentContainer !== bridge.lockedApoContainer) {
        unlock(currentContainer);
      }
      bridge.lockedApoContainer = null;
      return;
    }

    const container = findApoOptionContainer();
    if (!container) return;
    if (
      bridge.lockedApoContainer &&
      bridge.lockedApoContainer !== container
    ) {
      unlock(bridge.lockedApoContainer);
    }

    bridge.lockedApoContainer = container;
    container.setAttribute('inert', '');
    container.setAttribute('aria-busy', 'true');
  };

  const invalidateIsolatedCheckout = () => {
    bridge.checkoutGeneration += 1;
    bridge.checkoutAbortController?.abort();
    bridge.checkoutAbortController = null;
    bridge.isolatedCheckoutPending = false;
    document.documentElement.classList.remove('joa-express-navigating');
    setApoConfigurationLocked(false);
    if (bridge.form) delete bridge.form.dataset.joaExpressNavigating;
  };

  const normalizedAttributeFingerprint = (attributes) =>
    JSON.stringify(
      (attributes || [])
        .map((attribute) => [
          String(attribute?.key || ''),
          String(attribute?.value || '')
        ])
        .sort(([left], [right]) => left.localeCompare(right))
    );

  const transientReadinessCodes = new Set([
    'MERCHANDISE_OUT_OF_STOCK',
    'MERCHANDISE_PRODUCT_NOT_PUBLISHED',
    'MERCHANDISE_NOT_APPLICABLE',
    'MERCHANDISE_NOT_ENOUGH_STOCK',
    'MERCHANDISE_NOT_ENOUGH_STOCK_AVAILABLE',
    'PENDING_PROCESS',
    'PENDING_DELIVERY_GROUPS',
    'SERVICE_UNAVAILABLE',
    'INTERNAL_SERVER_ERROR',
    'THROTTLED'
  ]);

  const makeCheckoutError = (message, { retryable = false, codes = [] } = {}) => {
    const error = new Error(message);
    error.joaRetryableReadiness = retryable;
    error.joaBackgroundRetry = retryable;
    error.joaCheckoutCodes = codes;
    return error;
  };

  const checkoutDetailCode = (detail) =>
    String(detail?.code || detail?.extensions?.code || '').toUpperCase();

  const invalidMerchandiseField = (detail) => {
    if (checkoutDetailCode(detail) !== 'INVALID') return false;
    const field = Array.isArray(detail?.field)
      ? detail.field.join('.')
      : String(detail?.field || '');
    return /(?:^|\.)merchandiseId$/i.test(field);
  };

  const retryableCheckoutDetails = (details) =>
    details.some((detail) =>
      transientReadinessCodes.has(checkoutDetailCode(detail)) ||
      invalidMerchandiseField(detail)
    );

  const abortableDelay = (delay, signal) => new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const onAbort = () => {
      window.clearTimeout(timer);
      if (bridge.readinessTimer === timer) bridge.readinessTimer = null;
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      if (bridge.readinessTimer === timer) bridge.readinessTimer = null;
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, delay);
    bridge.readinessTimer = timer;
    signal.addEventListener('abort', onAbort, { once: true });
  });

  const configuredSourcePriceQuery = `
    query ConfiguredSourcePrice($id: ID!) {
      node(id: $id) {
        ... on ProductVariant { id price { amount currencyCode } }
      }
    }
  `;

  const configuredSourcePriceMatches = async ({ parentVariantId, source, storefrontToken, signal }) => {
    // Without @inContext, Shopify returns the shop currency. This additional
    // read is only needed on a conversion mismatch; matching quotes keep one request.
    const id = `gid://shopify/ProductVariant/${parentVariantId}`;
    let result;
    try {
      result = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST', credentials: 'omit', signal,
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken },
        body: JSON.stringify({ query: configuredSourcePriceQuery, variables: { id } })
      });
      if (!result.ok) return false;
      const data = await result.json();
      const variant = data?.data?.node;
      return !data?.errors?.length && variant?.id === id &&
        variant?.price?.currencyCode === 'USD' &&
        minorAmountsMatch(Math.round(Number(variant.price.amount) * 100), source.minorUnits);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return false;
    }
  };

  const createAndValidateIsolatedCart = async ({
    response,
    form,
    selectionFingerprint,
    payloadFingerprint,
    signal,
    requiredState,
    isCurrent,
    allowDeposit = false
  }) => {
    const initConfig = bridge.commonData?.initConfig || window.mwApoInit;
    const storefrontToken = String(initConfig?.settings?.store_front_token || '');
    const parentVariantId = String(response?.variant_id ?? response?.variantId ?? '');
    const parentQuantity = Number(response?.qty ?? response?.quantity);
    const { country: buyerCountry, currency: buyerCurrency } = currentPriceContext();
    const preparationFingerprint = currentPreparationFingerprint(bridge.apoForm);

    if (
      !form ||
      form.dataset.joaExpressState !== requiredState ||
      !productCanCheckout(form) ||
      (!allowDeposit && isDepositSelected()) ||
      !response ||
      !/^[a-f0-9]{32}$/i.test(storefrontToken) ||
      !/^[1-9]\d+$/.test(parentVariantId) ||
      parentQuantity !== 1 ||
      !/^[A-Z]{2}$/.test(buyerCountry) ||
      !selectionFingerprint ||
      selectionFingerprint !== bridge.preparedSelectionFingerprint ||
      typeof isCurrent !== 'function' ||
      !isCurrent()
    ) {
      throw makeCheckoutError('The isolated checkout payload is incomplete');
    }

    const expectedAttributes = preparedLineAttributes(response);
    const parentInput = {
      merchandiseId: `gid://shopify/ProductVariant/${parentVariantId}`,
      quantity: parentQuantity,
      attributes: expectedAttributes
    };
    const stones = birthstoneSelection();
    const childInput = stones ? window.JoaBirthstones?.childFor(parentInput, response.properties, true) : null;
    if (stones && ((!stones.included && !childInput) || stones.currency !== buyerCurrency)) {
      throw makeCheckoutError('The birthstone checkout payload is incomplete');
    }
    const checkoutLines = childInput ? [parentInput, childInput] : [parentInput];
    let result;
    try {
      result = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken
        },
        body: JSON.stringify({
          query: isolatedCheckoutMutation,
          variables: {
            input: {
              buyerIdentity: { countryCode: buyerCountry },
              lines: checkoutLines
            }
          }
        }),
        signal
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      throw makeCheckoutError('Shopify checkout readiness could not be reached', {
        retryable: true
      });
    }

    const retryableHttpStatuses = new Set([408, 425, 429, 500, 502, 503, 504]);
    if (!result.ok) {
      throw makeCheckoutError(`Isolated checkout failed (${result.status})`, {
        retryable: retryableHttpStatuses.has(result.status),
        codes: [`HTTP_${result.status}`]
      });
    }

    let data;
    try {
      data = await result.json();
    } catch (_error) {
      throw makeCheckoutError('Shopify returned an incomplete readiness response', {
        retryable: true
      });
    }

    const payload = data?.data?.cartCreate;
    const cart = payload?.cart;
    const errors = [...(data?.errors || []), ...(payload?.userErrors || [])];
    const warnings = payload?.warnings || [];
    const details = [...errors, ...warnings];
    const codes = details
      .map(checkoutDetailCode)
      .filter(Boolean);

    if (errors.length) {
      throw makeCheckoutError(
        errors[0]?.message || 'Shopify could not prepare this configured item',
        {
          retryable: retryableCheckoutDetails(errors),
          codes
        }
      );
    }

    // Shopify can return a zero-quantity line while APO's generated variant
    // propagates. Its transient warning must be retried before strict line checks.
    if (retryableCheckoutDetails(warnings)) {
      throw makeCheckoutError(warnings[0]?.message || 'The configured item is still preparing', {
        retryable: true,
        codes
      });
    }

    if (!cart || !Array.isArray(cart?.lines?.nodes) || cart.lines.nodes.length === 0) {
      throw makeCheckoutError(
        warnings[0]?.message || 'The configured item is not ready for checkout',
        {
          retryable:
            warnings.length === 0 || retryableCheckoutDetails(warnings),
          codes
        }
      );
    }

    const line = cart.lines.nodes.find(item => item.merchandise?.id === parentInput.merchandiseId);
    const child = childInput ? cart.lines.nodes.find(item => item.merchandise?.id === childInput.merchandiseId) : null;
    const childMinorUnits = child ? Math.round(Number(child.cost?.totalAmount?.amount) * currencyMinorScale(buyerCurrency)) : 0;
    const childMatches = !childInput || Boolean(
      child && child.quantity === childInput.quantity &&
      child.parentRelationship?.parent?.id === line?.id &&
      normalizedAttributeFingerprint(child.attributes) === normalizedAttributeFingerprint(childInput.attributes) &&
      child.cost?.totalAmount?.currencyCode === buyerCurrency &&
      minorAmountsMatch(childMinorUnits, stones.total)
    );
    const lineVariantId = String(line?.merchandise?.id || '').split('/').pop();
    const lineProductId = String(line?.merchandise?.product?.id || '').split('/').pop();
    const lineProductHandle = String(line?.merchandise?.product?.handle || '');
    let checkoutUrl;
    try {
      checkoutUrl = new URL(String(cart?.checkoutUrl || ''), window.location.origin);
    } catch (_error) {
      throw makeCheckoutError('Shopify returned an invalid checkout address');
    }
    const subtotalCurrency = String(cart?.cost?.subtotalAmount?.currencyCode || '');
    const subtotalMinorUnits = Math.round(
      Number(cart?.cost?.subtotalAmount?.amount) *
      currencyMinorScale(subtotalCurrency)
    );
    const expectedMinorUnits = displayedProductPriceCents(subtotalCurrency) + (stones?.total || 0);
    const shopDomain = String(initConfig?.shop_domain || '').toLowerCase();
    const allowedCheckoutHosts = new Set(
      [window.location.hostname.toLowerCase(), shopDomain].filter(Boolean)
    );
    const responseIsCurrent = () =>
      isCurrent() &&
      bridge.preparedResponse === response &&
      preparedResponseFingerprint(bridge.preparedResponse) === payloadFingerprint &&
      currentApoSelectionFingerprint(bridge.apoForm) === selectionFingerprint &&
      selectionFingerprint === bridge.preparedSelectionFingerprint &&
      preparationFingerprint === currentPreparationFingerprint(bridge.apoForm) &&
      form.dataset.joaExpressState === requiredState &&
      (allowDeposit || !isDepositSelected());
    const responseStillCurrent = responseIsCurrent();
    const expectedProductId = String(response.__joaExpectedProductId || '');
    const expectedProductHandle = String(response.__joaExpectedProductHandle || '');
    const attributesMatch =
      normalizedAttributeFingerprint(line?.attributes) ===
      normalizedAttributeFingerprint(expectedAttributes);
    const checkoutPathValid =
      checkoutUrl.pathname.includes('/checkouts/') ||
      checkoutUrl.pathname.startsWith('/cart/c/');

    debug('isolated checkout response', JSON.stringify({
      codes,
      warnings: warnings.map((warning) => warning?.message).filter(Boolean),
      lineCount: cart?.lines?.nodes?.length,
      lineQuantity: line?.quantity,
      lineVariantId,
      parentVariantId,
      lineProductId,
      expectedProductId,
      attributesMatch,
      responseStillCurrent,
      subtotalMinorUnits,
      expectedMinorUnits,
      subtotalCurrency,
      checkoutProtocol: checkoutUrl.protocol,
      checkoutHost: checkoutUrl.hostname,
      checkoutPathValid
    }));

    const lineMatches =
      cart.lines.nodes.length === checkoutLines.length &&
      childMatches &&
      line?.quantity === 1 &&
      lineVariantId === parentVariantId;
    const productMatches =
      (!expectedProductId || lineProductId === expectedProductId) &&
      (!expectedProductHandle || lineProductHandle === expectedProductHandle);
    const checkoutAddressMatches =
      checkoutUrl.protocol === 'https:' &&
      allowedCheckoutHosts.has(checkoutUrl.hostname.toLowerCase()) &&
      checkoutPathValid;
    let subtotalMatches =
      minorAmountsMatch(subtotalMinorUnits, expectedMinorUnits);
    const currencyMatches = subtotalCurrency === buyerCurrency;

    let sourcePriceProven = response.__joaRequiresSourcePriceProof !== true;
    if ((!subtotalMatches || !sourcePriceProven) && lineMatches && productMatches && attributesMatch &&
      responseStillCurrent && checkoutAddressMatches && currencyMatches &&
      Number.isSafeInteger(subtotalMinorUnits) && subtotalMinorUnits > 0) {
      // Only reconcile a conversion difference after proving the configured
      // variant has the exact unconverted configuration price. Never accept an
      // arbitrary subtotal just because Shopify has created the variant.
      const source = currentSourcePrice();
      if (source && subtotalCurrency !== String(initConfig?.shop_currency || '').toUpperCase() &&
        typeof response.properties?.__mws_apo_cta !== 'string') {
        sourcePriceProven = await configuredSourcePriceMatches({
          parentVariantId, source, storefrontToken, signal
        });
        if (sourcePriceProven) subtotalMatches = true;
      }
    }

    if (
      !lineMatches ||
      !productMatches ||
      !attributesMatch ||
      !responseIsCurrent() ||
      !subtotalMatches ||
      !sourcePriceProven ||
      !currencyMatches ||
      !checkoutAddressMatches
    ) {
      const onlySubtotalIsPending =
        lineMatches &&
        productMatches &&
        attributesMatch &&
        responseIsCurrent() &&
        currencyMatches &&
        checkoutAddressMatches &&
        !subtotalMatches;
      throw makeCheckoutError('The isolated checkout could not be verified', {
        retryable: onlySubtotalIsPending,
        codes: onlySubtotalIsPending ? ['STOREFRONT_PRICE_PENDING'] : []
      });
    }

    const verifiedCart = {
      cartId: String(cart.id || ''),
      checkoutUrl,
      // Price displays add birthstones separately; keep the shared ring quote
      // as the parent price while validating the complete checkout total above.
      subtotalMinorUnits: subtotalMinorUnits - childMinorUnits,
      subtotalCurrency,
      verifiedAt: Date.now(),
      preparationFingerprint
    };
    bridge.priceQuote = verifiedCart;
    applyVerifiedPrice();
    debug('shared price verified', JSON.stringify({
      variantId: parentVariantId, country: buyerCountry, currency: subtotalCurrency,
      displayedBefore: expectedMinorUnits, verifiedMinorUnits: subtotalMinorUnits,
      sourceMinorUnits: currentSourcePrice()?.minorUnits ?? null,
      reconciled: expectedMinorUnits !== subtotalMinorUnits, depositSelected: isDepositSelected()
    }));
    return verifiedCart;
  };

  const startIsolatedCheckout = async () => {
    if (bridge.isolatedCheckoutPending) return;

    const form = neutralizeExpressForm();
    const response = bridge.preparedResponse;
    const parentVariantId = String(response?.variant_id ?? response?.variantId ?? '');
    const selectionFingerprint =
      currentApoSelectionFingerprint(bridge.apoForm);
    const payloadFingerprint = preparedResponseFingerprint(response);

    if (
      !form ||
      form.dataset.joaExpressState !== 'ready' ||
      !productCanCheckout(form) ||
      isDepositSelected() ||
      !response ||
      !selectionFingerprint ||
      selectionFingerprint !== bridge.preparedSelectionFingerprint ||
      !preparedPayloadIsVerified(form, response)
    ) {
      setFormState(form, 'preparing');
      window.setTimeout(schedulePreparation, 0);
      throw new Error('The isolated checkout payload is incomplete');
    }

    const generation = bridge.checkoutGeneration;
    const controller = new AbortController();
    bridge.checkoutAbortController = controller;
    bridge.isolatedCheckoutPending = true;
    form.dataset.joaExpressNavigating = 'true';
    document.documentElement.classList.add('joa-express-navigating');
    setApoConfigurationLocked(true);
    scheduleControlSync(0);

    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CHECKOUT_TIMEOUT);

    try {
      const verifiedCart = await createAndValidateIsolatedCart({
        response,
        form,
        selectionFingerprint,
        payloadFingerprint,
        signal: controller.signal,
        requiredState: 'ready',
        isCurrent: () => generation === bridge.checkoutGeneration
      });

      debug('isolated checkout verified', {
        parentVariantId,
        subtotalMinorUnits: verifiedCart.subtotalMinorUnits,
        subtotalCurrency: verifiedCart.subtotalCurrency,
        checkoutHost: verifiedCart.checkoutUrl.hostname
      });
      window.location.assign(verifiedCart.checkoutUrl.href);
    } catch (error) {
      if (
        generation !== bridge.checkoutGeneration ||
        (error?.name === 'AbortError' && !timedOut)
      ) {
        return;
      }

      bridge.isolatedCheckoutPending = false;
      delete form.dataset.joaExpressNavigating;
      document.documentElement.classList.remove('joa-express-navigating');
      setApoConfigurationLocked(false);
      setFormState(form, 'preparing');
      window.setTimeout(schedulePreparation, 0);
      if (window.console) {
        console.warn(
          '[JOA express] Isolated checkout unavailable',
          timedOut ? new Error('Checkout request timed out') : error
        );
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
      if (bridge.checkoutAbortController === controller) {
        bridge.checkoutAbortController = null;
      }
    }
  };

  const hasGenericShopifyChoice = (paymentButton) => {
    const wallet = paymentButton?.querySelector('shopify-accelerated-checkout');
    if (!wallet || wallet.getAttribute('recommended') !== 'null') return false;
    try {
      return JSON.parse(wallet.getAttribute('fallback') || 'null')?.name === 'buy_it_now';
    } catch (_error) {
      return false;
    }
  };

  const syncExpressControl = () => {
    const form = neutralizeExpressForm();
    if (!form) return;

    const existing = form.querySelector('[data-joa-isolated-checkout-button]');
    const paymentButton = form.querySelector('.shopify-payment-button');
    const state = form.dataset.joaExpressState;
    const ready =
      state === 'ready' &&
      productCanCheckout(form) &&
      !isDepositSelected() &&
      preparedPayloadIsVerified(form);
    if (paymentButton) {
      paymentButton.inert = !ready;
      paymentButton.toggleAttribute('inert', !ready);
    }
    const brandTemplate = form.querySelector('[data-joa-checkout-brand-template]');
    const ensureFallbackButton = (label) => {
      let button = existing;
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'joa-isolated-checkout-button';
        button.dataset.joaIsolatedCheckoutButton = '';
      }

      if (!bridge.isolatedButtons.has(button)) {
        bridge.isolatedButtons.add(button);
        button.addEventListener('pointerdown', pinPreparedPayload, true);
        button.addEventListener('touchstart', pinPreparedPayload, true);
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          pinPreparedPayload();
          startIsolatedCheckout().catch(() => {});
        }, true);
      }

      if (!button.isConnected) {
        if (paymentButton) paymentButton.insertAdjacentElement('afterend', button);
        else form.appendChild(button);
      }

      button.disabled = bridge.isolatedCheckoutPending;
      const pending = bridge.isolatedCheckoutPending;
      const nextLabel = pending
        ? brandTemplate?.dataset.openingLabel || 'Opening checkout…'
        : brandTemplate?.dataset.checkoutLabel || label;
      button.setAttribute('aria-busy', String(pending));
      button.setAttribute('aria-label', nextLabel);
      const presentation = pending ? 'pending' : brandTemplate ? 'brand' : 'text';
      button.classList.toggle('joa-checkout-brand-button', Boolean(brandTemplate));
      if (button.dataset.presentation !== presentation) {
        button.dataset.presentation = presentation;
        if (presentation === 'brand') {
          button.replaceChildren(brandTemplate.content.cloneNode(true));
        } else if (pending) {
          const spinner = document.createElement('span');
          spinner.className = 'joa-checkout-brand-spinner';
          spinner.setAttribute('aria-hidden', 'true');
          const message = document.createElement('span');
          message.textContent = nextLabel;
          button.replaceChildren(spinner, message);
        } else {
          button.textContent = nextLabel;
        }
      }
      return button;
    };

    if (!ready) {
      if (form.classList.contains('joa-isolated-fallback')) form.classList.remove('joa-isolated-fallback');
      form.dataset.joaWalletState = 'resolving';
      paymentButton?.style.removeProperty('display');
      existing?.remove();
      return;
    }

    if (paymentButton && !birthstoneSelection() && !hasGenericShopifyChoice(paymentButton)) {
      if (form.classList.contains('joa-isolated-fallback')) form.classList.remove('joa-isolated-fallback');
      form.dataset.joaWalletState = 'native';
      paymentButton.style.removeProperty('display');
      existing?.remove();
      return;
    }

    // A personalized order has a ring plus its linked add-on. Send that verified
    // isolated cart to Shopify checkout; a native single-product form omits it.
    if (!form.classList.contains('joa-isolated-fallback')) form.classList.add('joa-isolated-fallback');
    form.dataset.joaWalletState = 'fallback';
    if (paymentButton) paymentButton.style.setProperty('display', 'none', 'important');
    ensureFallbackButton(form.querySelector('[data-joa-express-status]')?.dataset.buyNowLabel || 'Buy now');
  };

  const scheduleControlSync = (delay = 0) => {
    window.clearTimeout(bridge.controlTimer);
    bridge.controlTimer = window.setTimeout(() => {
      bridge.controlTimer = null;
      pinPreparedPayload();
      syncExpressControl();
    }, Math.max(0, delay));
  };

  const verifyPreparedCheckout = async ({
    form,
    response,
    selectionFingerprint,
    requestVersion
  }) => {
    invalidateReadiness();
    const generation = bridge.readinessGeneration;
    const payloadFingerprint = preparedResponseFingerprint(response);
    const controller = new AbortController();
    bridge.readinessAbortController = controller;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, READINESS_TIMEOUT);
    const isCurrent = () =>
      generation === bridge.readinessGeneration &&
      requestVersion === bridge.requestVersion;

    try {
      for (let attempt = 0; attempt < READINESS_RETRY_DELAYS.length; attempt += 1) {
        const delay = READINESS_RETRY_DELAYS[attempt];
        if (delay > 0) await abortableDelay(delay, controller.signal);

        try {
          const verifiedCart = await createAndValidateIsolatedCart({
            response,
            form,
            selectionFingerprint,
            payloadFingerprint,
            signal: controller.signal,
            requiredState: 'verifying',
            isCurrent,
            // Verify the full configured price for either payment choice.
            // Downpay continues to own the real cart's selected selling plan.
            allowDeposit: true
          });

          if (!isCurrent()) {
            throw new DOMException('Aborted', 'AbortError');
          }

          bridge.verifiedResponseFingerprint = payloadFingerprint;
          bridge.verifiedSelectionFingerprint = selectionFingerprint;
          bridge.verifiedPreparationFingerprint = verifiedCart.preparationFingerprint;
          debug('checkout readiness verified', {
            attempt: attempt + 1,
            cartId: verifiedCart.cartId
          });
          return verifiedCart;
        } catch (error) {
          if (error?.name === 'AbortError' || !isCurrent()) throw error;
          const canRetry =
            error?.joaRetryableReadiness === true &&
            attempt < READINESS_RETRY_DELAYS.length - 1;
          if (!canRetry) {
            error.joaPrepareRetry = false;
            throw error;
          }

          debug('waiting for generated variant readiness', {
            attempt: attempt + 1,
            nextDelay: READINESS_RETRY_DELAYS[attempt + 1],
            codes: error.joaCheckoutCodes || [],
            reason: String(error?.message || error)
          });
        }
      }
    } catch (error) {
      if (timedOut && error?.name === 'AbortError') {
        const timeoutError = makeCheckoutError(
          'The configured item did not become ready for checkout in time'
        );
        timeoutError.joaPrepareRetry = false;
        timeoutError.joaBackgroundRetry = true;
        throw timeoutError;
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
      if (bridge.readinessAbortController === controller) {
        window.clearTimeout(bridge.readinessTimer);
        bridge.readinessTimer = null;
        bridge.readinessAbortController = null;
      }
    }

    const error = makeCheckoutError('Checkout readiness could not be verified');
    error.joaPrepareRetry = false;
    throw error;
  };

  const scheduleAutomaticRecovery = ({
    form,
    apoForm,
    selectionFingerprint,
    version,
    error
  }) => {
    const canRecover =
      error?.joaBackgroundRetry === true &&
      bridge.backgroundRetryAttempt < MAX_BACKGROUND_RETRIES &&
      version === bridge.requestVersion &&
      productCanCheckout(form) &&
      requiredOptionsAreValid(apoForm) &&
      selectionFingerprint === currentApoSelectionFingerprint(apoForm);

    if (!canRecover) return false;

    if (!navigator.onLine || document.hidden) {
      suspendPreparation(form);
      debug('checkout recovery paused', {
        offline: !navigator.onLine,
        hidden: document.hidden
      });
      return true;
    }

    const retryIndex = Math.min(
      bridge.backgroundRetryAttempt,
      BACKGROUND_RETRY_DELAYS.length - 1
    );
    const delay = BACKGROUND_RETRY_DELAYS[retryIndex];
    bridge.backgroundRetryAttempt += 1;
    setFormState(form, 'preparing');
    debug('continuing checkout preparation in background', {
      recoveryAttempt: bridge.backgroundRetryAttempt,
      delay,
      reason: String(error?.message || error)
    });
    window.clearTimeout(bridge.timer);
    bridge.timer = window.setTimeout(() => {
      bridge.timer = null;
      if (version === bridge.requestVersion) {
        prepareExpressCheckout(version, 0);
      }
    }, delay);
    return true;
  };

  const prepareExpressCheckout = async (version, attempt = 0) => {
    const { apoForm, expressForm: form } = findForms();
    const commonData = bridge.commonData;
    const loadedState = bridge.loadedState || commonData?.loadedState;
    const initConfig = commonData?.initConfig || window.mwApoInit;
    const selectionFingerprint = currentApoSelectionFingerprint(apoForm);

    if (version !== bridge.requestVersion) return;

    if (
      !form ||
      !apoForm ||
      !commonData ||
      !loadedState ||
      !productCanCheckout(form) ||
      !requiredOptionsAreValid(apoForm)
    ) {
      setFormState(form, 'waiting');
      return;
    }

    const shopDomain = String(initConfig?.shop_domain || '');
    if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shopDomain)) {
      setFormState(form, 'unavailable');
      debug('invalid shop domain', shopDomain);
      return;
    }

    setFormState(form, 'preparing');
    const controller = new AbortController();
    bridge.abortController = controller;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, PREPARE_TIMEOUT);

    try {
      let result;
      try {
        result = await fetch(
          `${APO_ENDPOINT_ORIGIN}/front/product/${shopDomain}/cart`,
          {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              state: clonePreparationState(loadedState),
              shop_cart: shopCartForPreparation(initConfig?.cart),
              locale: initConfig?.locale_primary ? null : initConfig?.locale || null
            }),
            signal: controller.signal
          }
        );
      } catch (error) {
        if (error?.name !== 'AbortError') error.joaBackgroundRetry = true;
        throw error;
      }

      if (!result.ok) {
        const details = await result.json().catch(() => null);
        debug('APO preparation rejected', JSON.stringify({ status: result.status, details }));
        const error = new Error(`APO preparation failed (${result.status})`);
        error.joaBackgroundRetry =
          [408, 425, 429, 500, 502, 503, 504].includes(result.status);
        if (!error.joaBackgroundRetry) error.joaPrepareRetry = false;
        throw error;
      }

      let response;
      try {
        response = await result.json();
      } catch (error) {
        error.joaBackgroundRetry = true;
        throw error;
      }
      window.clearTimeout(timeout);
      if (version !== bridge.requestVersion) return;
      await new Promise((resolve) =>
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
      );
      if (version !== bridge.requestVersion) return;
      if (
        selectionFingerprint !== currentApoSelectionFingerprint(apoForm)
      ) {
        throw new Error('The ring configuration changed during preparation');
      }

      const normalizedResponse = normalizeResponseCurrency(
        response,
        commonData,
        initConfig
      );
      const engraving = engravingValue(apoForm);
      if (engraving) normalizedResponse.properties.Engraving = engraving;
      else delete normalizedResponse.properties.Engraving;
      for (const name of ['Engraving font', 'Stone placement', 'Personalization layout', 'Birthstones', '_joa_personalization_layout']) {
        const control = apoForm.querySelector(`[name="properties[${name}]"]`);
        if (control && !control.disabled && control.value) normalizedResponse.properties[name] = control.value;
        else delete normalizedResponse.properties[name];
      }
      const stones = birthstoneSelection(apoForm);
      if (stones) Object.assign(normalizedResponse.properties, stones.properties);
      installPreparedPayload(form, normalizedResponse);
      setFormState(form, 'verifying');
      await verifyPreparedCheckout({
        form,
        response: normalizedResponse,
        selectionFingerprint,
        requestVersion: version
      });
      if (version !== bridge.requestVersion) return;
      bridge.backgroundRetryAttempt = 0;
      setFormState(form, 'ready');
    } catch (error) {
      if (version !== bridge.requestVersion) return;
      if (error?.name === 'AbortError' && !timedOut) return;

      clearPreparedPayload(form);
      const retryDelay = PREPARE_RETRY_DELAYS[attempt];
      const canRetry =
        retryDelay != null &&
        error?.joaPrepareRetry !== false &&
        productCanCheckout(form) &&
        requiredOptionsAreValid(apoForm) &&
        selectionFingerprint === currentApoSelectionFingerprint(apoForm);

      if (canRetry) {
        setFormState(form, 'preparing');
        debug('retrying preparation', {
          attempt: attempt + 2,
          delay: retryDelay,
          reason: timedOut ? 'timeout' : String(error?.message || error)
        });
        bridge.timer = window.setTimeout(
          () => prepareExpressCheckout(version, attempt + 1),
          retryDelay
        );
        return;
      }

      const recoveryError = timedOut
        ? Object.assign(new Error('APO preparation timed out'), {
            joaBackgroundRetry: true
          })
        : error;
      if (
        scheduleAutomaticRecovery({
          form,
          apoForm,
          selectionFingerprint,
          version,
          error: recoveryError
        })
      ) {
        return;
      }

      setFormState(form, 'unavailable');
      bridge.preparationRecoveryAllowed = recoveryError?.joaBackgroundRetry === true;
      if (window.console) {
        console.warn(
          '[JOA express] Checkout preparation unavailable',
          timedOut ? new Error('APO preparation timed out') : error
        );
      }
    } finally {
      window.clearTimeout(timeout);
      if (bridge.abortController === controller) {
        bridge.abortController = null;
      }
    }
  };

  const schedulePreparation = () => {
    if (bridge.cartConfigurationBusy) return;
    const { apoForm, expressForm: form } = findForms();
    if (bridge.preparationSuspended && !preparationCanRun()) return;
    bridge.preparationSuspended = false;
    const selectionFingerprint = currentApoSelectionFingerprint(apoForm);
    const preparationFingerprint = currentPreparationFingerprint(apoForm);
    if (
      form &&
      selectionFingerprint &&
      preparationFingerprint === bridge.preparationFingerprint &&
      (busyExpressState(form.dataset.joaExpressState) ||
        preparedPayloadIsVerified(form) ||
        preparationFingerprint === bridge.exhaustedSelectionFingerprint)
    ) {
      applyVerifiedPrice();
      setFormState(form, form.dataset.joaExpressState);
      return;
    }
    invalidateIsolatedCheckout();
    stopPendingRequest();
    clearPreparedPayload(form);

    if (
      !bridge.apoActive ||
      !bridge.commonData ||
      !bridge.loadedState ||
      !form ||
      !apoForm ||
      !productCanCheckout(form) ||
      !requiredOptionsAreValid(apoForm)
    ) {
      setFormState(form, 'waiting');
      return;
    }

    if (!preparationCanRun()) {
      suspendPreparation(form);
      return;
    }

    setFormState(form, 'preparing');
    const version = bridge.requestVersion;
    bridge.preparationFingerprint = preparationFingerprint;
    bridge.preparationDeadlineTimer = window.setTimeout(() => {
      if (version !== bridge.requestVersion) return;
      stopPendingRequest();
      clearPreparedPayload(form);
      bridge.preparationFingerprint = preparationFingerprint;
      bridge.exhaustedSelectionFingerprint = preparationFingerprint;
      setFormState(form, 'unavailable');
      bridge.preparationRecoveryAllowed = true;
      console.warn('[JOA express] Preparation deadline reached; configured Add to cart remains available');
    }, TOTAL_PREPARATION_TIMEOUT);
    bridge.timer = window.setTimeout(
      () => prepareExpressCheckout(version, 0),
      PREPARE_DELAY
    );
  };

  const disableApoBuyNow = (init) => {
    if (!init?.settings?.beta_features) return false;
    init.settings.beta_features.use_buy_now = 'no';
    init.settings.selectors = init.settings.selectors || {};
    init.settings.selectors.buy_it_now =
      '[data-joa-apo-buy-now-disabled]';
    return true;
  };

  const cartLinePaymentPlan = (item) => String(
    item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan || ''
  );

  const cartForPaymentPlan = (cart, planId, engraving, personalization = {}) => {
    const source = Array.isArray(cart?.items) ? cart.items : [];
    const groupId = (item) => String(item?.properties?._mws_cart || item?.properties?.__mws_apo_cart || '');
    const parents = source.filter((item) => !item?.properties?._mws_option);
    const allowedGroups = new Set(parents.filter((item) => cartLinePaymentPlan(item) === planId && String(item.properties?.Engraving || '') === engraving && Object.entries(personalization).every(([key,value]) => String(item.properties?.[key] || '') === value)).map(groupId));
    const items = source.filter((item) => item?.properties?._mws_option
      ? allowedGroups.has(groupId(item))
      : cartLinePaymentPlan(item) === planId && String(item.properties?.Engraving || '') === engraving && Object.entries(personalization).every(([key,value]) => String(item.properties?.[key] || '') === value)
    ).map((item) => ({ ...item, properties: { ...(item.properties || {}) } }));
    return { ...cart, items, item_count: items.reduce((count, item) => count + Number(item.quantity || 0), 0) };
  };

  const selectedCartPaymentPlan = (form) => {
    if (document.querySelector('#purchase-option-full')?.checked) return '';
    if (!document.querySelector('#purchase-option-deposit-input')?.checked) return null;
    const input = form?.querySelector('[name="selling_plan"]');
    const id = String(input?.value || window.downpay?.selected_selling_plan_id || '');
    return /^\d+$/.test(id) ? id : null;
  };

  let cartPaymentOperation = null;

  const finishCartPaymentOperation = () => {
    const operation = cartPaymentOperation;
    if (!operation) return;
    debug('payment cart finished', JSON.stringify({ planId: operation.planId }));
    operation.form.removeEventListener('formdata', operation.applyPlan);
    Object.defineProperty(operation.init, 'cart', {
      ...operation.descriptor,
      value: operation.rawCart
    });
    operation.controls.forEach(({ input, disabled }) => { input.disabled = disabled; });
    cartPaymentOperation = null;
  };

  const beginCartPaymentOperation = (commonData) => {
    const form = commonData?.htmlElements?.form;
    const init = commonData?.initConfig;
    const planId = selectedCartPaymentPlan(form);
    if (!form || !init || planId === null || cartPaymentOperation) {
      debug('payment cart start skipped', JSON.stringify({ form: Boolean(form), init: Boolean(init), planId, active: Boolean(cartPaymentOperation) }));
      return;
    }
    const descriptor = Object.getOwnPropertyDescriptor(init, 'cart');
    if (!descriptor || !('value' in descriptor) || descriptor.configurable === false) {
      debug('payment cart descriptor unavailable', JSON.stringify({ exists: Boolean(descriptor), data: Boolean(descriptor && 'value' in descriptor), configurable: descriptor?.configurable }));
      return;
    }
    debug('payment cart started', JSON.stringify({ planId }));
    const operation = {
      init, form, planId, descriptor, rawCart: init.cart, engraving: engravingValue(form), personalization: Object.fromEntries(Array.from(form.querySelectorAll('[data-personalization-property]')).map(input => [input.name.slice(11,-1),input.disabled?'':input.value])),
      controls: Array.from(document.querySelectorAll('#purchase-option-full, #purchase-option-deposit-input'))
        .map((input) => ({ input, disabled: input.disabled })),
      applyPlan: (event) => {
        event.formData.delete('selling_plan');
        if (planId) event.formData.set('selling_plan', planId);
      }
    };
    cartPaymentOperation = operation;
    Object.defineProperty(init, 'cart', {
      configurable: true,
      enumerable: descriptor.enumerable,
      get: () => {
        const filtered = cartForPaymentPlan(operation.rawCart, operation.planId, operation.engraving, operation.personalization);
        debug('payment cart read', JSON.stringify({ planId: operation.planId, sourcePlans: operation.rawCart?.items?.map(cartLinePaymentPlan), exposedPlans: filtered.items.map(cartLinePaymentPlan), quantities: filtered.items.map(item => item.quantity) }));
        return filtered;
      },
      set: (cart) => { operation.rawCart = cart; }
    });
    operation.controls.forEach(({ input }) => { input.disabled = true; });
    form.addEventListener('formdata', operation.applyPlan);
  };

  const releaseCartConfiguration = () => {
    window.clearTimeout(bridge.cartConfigurationUnlockTimer);
    bridge.cartConfigurationUnlockTimer = null;
    bridge.cartConfigurationBusy = false;
    bridge.cartConfigurationControls.forEach((control) => { control.disabled = false; });
    bridge.cartConfigurationControls = [];
    setApoConfigurationLocked(bridge.isolatedCheckoutPending);
    if (bridge.apoActive) schedulePreparation();
  };

  const setCartConfigurationBusy = (busy) => {
    window.clearTimeout(bridge.cartConfigurationUnlockTimer);
    if (!busy) {
      if (bridge.cartConfigurationBusy) {
        bridge.cartConfigurationUnlockTimer = window.setTimeout(releaseCartConfiguration, 150);
      }
      return;
    }
    if (bridge.cartConfigurationBusy) return;
    bridge.cartConfigurationBusy = true;
    setApoConfigurationLocked(true);
  };

  const guardCartConfiguration = (event) => {
    const button = event.target.closest?.('.mw-apo-add-to-cart-cloned, .mw-apo-sticky-add-to-cart-cloned, .sticky-add-to-cart__button, .joa-sticky-purchase');
    if (!button || bridge.cartConfigurationBusy) return;
    const form = button.closest('form') || bridge.apoForm || bridge.commonData?.htmlElements?.form;
    if (apoSelectionStateMatches(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    document.dispatchEvent(new CustomEvent('joa:cart-add-error'));
    clearPreparedPayload(bridge.form);
    setFormState(bridge.form, 'unavailable');
    const error = form?.querySelector('[ref="addToCartTextError"]');
    if (error) {
      error.textContent = typeof Theme === 'object' ? Theme.translations.configuration_out_of_sync : '';
      error.dataset.joaConfigurationError = 'true';
      error.setAttribute('role', 'alert');
      error.classList.remove('hidden');
    }
  };

  document.addEventListener('click', guardCartConfiguration, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.mw-apo-add-to-cart-cloned, .mw-apo-sticky-add-to-cart-cloned, .sticky-add-to-cart__button, .joa-sticky-purchase');
    if (!button || cartPaymentOperation || !document.querySelector('#purchase-option-deposit-input')?.checked) return;
    const form = button.closest('form') || bridge.commonData?.htmlElements?.form;
    if (selectedCartPaymentPlan(form) !== null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    document.dispatchEvent(new CustomEvent('joa:cart-add-error'));
    const error = form?.querySelector('[ref="addToCartTextError"]');
    if (error) {
      error.textContent = typeof Theme === 'object' ? Theme.translations.payment_option_loading : '';
      error.setAttribute('role', 'alert');
      error.classList.remove('hidden');
    }
  }, true);

  // The first pageshow can arrive after a fast customer starts an addition.
  // Only a restored page needs stale operation cleanup; an initial load must
  // not remove the payment-plan filter while APO is rebuilding the cart.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      finishCartPaymentOperation();
      releaseCartConfiguration();
    }
  });

  const installApoEventBridge = (init) => {
    if (!disableApoBuyNow(init)) return;
    if (init.onEvent?.__joaExpressWrapped) return;

    const previousOnEvent = init.onEvent;
    const joaApoEvent = function (name, payload) {
      disableApoBuyNow(init);

      if (name === 'attachEvents::initAddToCart::clickStart') {
        beginCartPaymentOperation(payload);
        setCartConfigurationBusy(true);
      }
      if (name === 'attachEvents::initAddToCart::clickEnd' || name === 'attachEvents::initAddToCart::clickError') {
        finishCartPaymentOperation();
        setCartConfigurationBusy(false);
      }
      if (name === 'attachEvents::initAddToCart::clickFinally') setCartConfigurationBusy(false);
      if (name === 'attachEvents::initAddToCart::clickError') document.dispatchEvent(new CustomEvent('joa:cart-add-error')); 

      if (name === 'init::finish' && payload?.htmlElements?.form && payload.loadedState) {
        apoFormStates.set(payload.htmlElements.form, snapshotApoState(payload.loadedState));
      }
      if (name === 'init::finish' && payload?.htmlElements?.form === findApoOptionContainer()?.closest('form')) {
        markApoAssigned('init::finish');
        bridge.commonData = payload;
        bridge.loadedState = apoFormStates.get(payload.htmlElements.form) || bridge.loadedState;
        window.setTimeout(() => {
          disableApoBuyNow(init);
          if (activateApoIfPresent()) schedulePreparation();
          else debug('APO finished without rendered option controls');
        }, 0);
      }

      if (name === 'initialize::no::options::for::product') {
        /*
         * APO emits this only after its product-options request succeeds and
         * confirms that the current product has no assigned options. Debounce
         * briefly so a secondary product block cannot outrun the main block.
         */
        scheduleApoNotApplicable(name);
      }

      if (name === 'initialize::options::request::failed') {
        /*
         * A request failure is not proof that APO is absent. Keep accelerated
         * checkout fail-closed so Shopify's unconfigured native Buy Now cannot
         * escape during an outage.
         */
        debug('APO assignment request failed; native checkout remains gated');
      }

      if (name === 'mwApo::ON_CHANGE_OPTION' && Array.isArray(payload)) {
        const nextState = payload[0];
        if (nextState?.values) {
          document.querySelectorAll('form').forEach(form => {
            const controls = Array.from(form.querySelectorAll('[name^="mw_opts["]'));
            // Only accept a published APO state that belongs to these option IDs
            // and agrees with this form's current visible choices.
            if (!controls.some(control => nextState.values[control.value]) || !apoSelectionStateMatches(form, nextState)) return;
            const snapshot = snapshotApoState(nextState);
            apoFormStates.set(form, snapshot);
            if (form === bridge.apoForm) bridge.loadedState = snapshot;
          });
        }
        if (apoSelectionStateMatches(bridge.apoForm)) {
          const error = bridge.apoForm?.querySelector('[data-joa-configuration-error]');
          if (error) {
            error.classList.add('hidden');
            delete error.dataset.joaConfigurationError;
          }
        }
        schedulePreparation();
      }

      if (name === 'init::insertIntoInline::afterHtmlInserted') {
        window.setTimeout(() => {
          disableApoBuyNow(init);
          if (activateApoIfPresent()) schedulePreparation();
        }, 0);
      }

      return typeof previousOnEvent === 'function'
        ? previousOnEvent.apply(this, arguments)
        : undefined;
    };

    joaApoEvent.__joaExpressWrapped = true;
    init.onEvent = joaApoEvent;
  };

  const previousCustom = window.mwApoCustom;
  window.mwApoCustom = function joaApoCustom() {
    const previousResult =
      typeof previousCustom === 'function'
        ? previousCustom.apply(this, arguments)
        : undefined;

    const init = window.mwApoInit;
    installApoEventBridge(init);
    window.setTimeout(() => disableApoBuyNow(init), 0);
    return previousResult;
  };

  bridge.activationObserver = new MutationObserver(() => {
    if (bridge.apoActive) return;
    if (activateApoIfPresent()) {
      bridge.activationObserver?.disconnect();
      bridge.activationObserver = null;
      window.setTimeout(schedulePreparation, 0);
    }
  });
  bridge.activationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  const apoBundleIsPresent = () =>
    Boolean(
      window.mwApoInit ||
      document.querySelector(
        'script[src*="mws_apo_bundle.js"], [data-mw-apo-custom-js]'
      )
    );

  const releaseOnlyWhenApoIsAbsent = () => {
    if (
      !bridge.apoActive &&
      !bridge.apoDecisionKnown &&
      !apoBundleIsPresent() &&
      !findApoOptionContainer()
    ) {
      markApoNotApplicable('APO app is absent');
    }
  };

  const scheduleEarlyNativeRelease = () => {
    window.setTimeout(releaseOnlyWhenApoIsAbsent, 3000);
  };

  if (document.readyState === 'complete') {
    scheduleEarlyNativeRelease();
  } else {
    window.addEventListener('load', scheduleEarlyNativeRelease, { once: true });
  }

  /*
   * This timer is an absence check, never an unconditional release. If APO is
   * installed but slow or failed, the original Buy Now remains safely gated.
   */
  bridge.nativeReleaseTimer = window.setTimeout(
    releaseOnlyWhenApoIsAbsent,
    8000
  );

  const apoConfigurationControlSelector = '[name^="mw_opts["]';
  const purchaseOptionControlSelector =
    '#purchase-option-deposit-input, #purchase-option-full';

  const gateCheckoutForConfigurationChange = () => {
    const form = neutralizeExpressForm();
    invalidateIsolatedCheckout();
    stopPendingRequest();
    clearPreparedPayload(form);
    if (
      form &&
      productCanCheckout(form) &&
      !isDepositSelected()
    ) {
      setFormState(form, 'preparing');
    } else {
      setFormState(form, 'waiting');
    }
  };

  const invalidateOnConfigurationInput = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches(apoConfigurationControlSelector)) {
      if (event.target.closest('form') !== (bridge.apoForm || findForms().apoForm)) return;
      if (preparedPayloadIsVerified(bridge.form)) return;
      /* APO publishes the new loadedState through mwApo::ON_CHANGE_OPTION.
       * Gate now, but never prepare from the pre-change state. */
      gateCheckoutForConfigurationChange();
      // A change may arrive after APO's callback. Reconcile after its handlers
      // finish, so a late DOM event cannot cancel preparation without restarting.
      window.setTimeout(schedulePreparation, 0);
      return;
    }

    if (!event.target.matches(purchaseOptionControlSelector)) return;

    // Both payment choices share the same full-price quote. Refresh wallet
    // gating without cancelling or duplicating an in-flight price request.
    const form = bridge.form;
    if (form) setFormState(form, form.dataset.joaExpressState);
    window.setTimeout(schedulePreparation, 0);
  };

  const onPersonalizationChange = (event) => {
    if (event.detail?.form !== (bridge.apoForm || findForms().apoForm)) return;
    if (bridge.apoSourcePrice) bridge.apoSourcePrice.selection = currentApoSelectionFingerprint(event.detail.form);
    gateCheckoutForConfigurationChange();
    schedulePreparation();
  };
  document.addEventListener('joa:engraving-change', onPersonalizationChange);
  document.addEventListener('joa:birthstones-change', onPersonalizationChange);

  document.addEventListener('input', invalidateOnConfigurationInput, true);
  document.addEventListener('change', invalidateOnConfigurationInput, true);

  /* Re-pin the payload after APO has applied its recalculated state and price. */
  document.addEventListener('mwApo::recalculated', (event) => {
    const detail = event.detail;
    const form = bridge.apoForm || findApoOptionContainer()?.closest('form');
    const selection = currentApoSelectionFingerprint(form);
    const minorUnits = Math.round(Number(detail?.price) * 100);
    if (String(detail?.productId || '') === pageProductId() && selection &&
      Number.isSafeInteger(minorUnits) && minorUnits > 0) {
      bridge.apoSourcePrice = { selection, minorUnits };
      applyVerifiedPrice();
    }
    window.setTimeout(schedulePreparation, 0);
  });

  document.addEventListener('downpay:selling-plan:change', () => {
    window.setTimeout(schedulePreparation, 0);
  });

  const expressEventDetails = (event) => {
    const form = neutralizeExpressForm();
    const path = typeof event.composedPath === 'function'
      ? event.composedPath()
      : [event.target];
    const isExpressEvent = Boolean(
      form && path.some((node) => node === form || (node?.nodeType === 1 && form.contains(node)))
    );
    return {
      form,
      isExpressEvent
    };
  };

  const pinOnExpressInteraction = (event) => {
    const {
      form,
      isExpressEvent
    } = expressEventDetails(event);
    if (!isExpressEvent || event.target.closest?.('[data-joa-express-retry]')) return;

    const state = form?.dataset.joaExpressState;
    if (state !== 'ready') {
      event.preventDefault();
      event.stopImmediatePropagation();

      /* The loading surface is intentionally inert while the current
       * configuration continues preparing in the background. */
      return;
    }

    if (pinPreparedPayload()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    gateCheckoutForConfigurationChange();
    schedulePreparation();
  };

  document.addEventListener('pointerdown', pinOnExpressInteraction, true);
  if (!('PointerEvent' in window)) {
    document.addEventListener('touchstart', pinOnExpressInteraction, {
      capture: true,
      passive: false
    });
  }

  document.addEventListener('click', (event) => {
    const {
      form,
      isExpressEvent
    } = expressEventDetails(event);
    if (!isExpressEvent) return;
    if (event.target.closest?.('[data-joa-express-retry]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (form?.dataset.joaExpressState === 'unavailable' && !isDepositSelected()) {
        stopPendingRequest();
        schedulePreparation();
      }
      return;
    }

    const wasReady = form?.dataset.joaExpressState === 'ready';

    if (
      !form ||
      !wasReady ||
      !productCanCheckout(form) ||
      !bridge.preparedResponse ||
      isDepositSelected() ||
      !preparedPayloadIsVerified(form) ||
      currentApoSelectionFingerprint(bridge.apoForm) !==
        bridge.preparedSelectionFingerprint
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();

      /* Keyboard, VoiceOver, and synthetic activation can arrive without a
       * pointerdown. If a control looked ready but failed the final freshness
       * checks, close it and visibly prepare the current selection again. */
      if (wasReady && productCanCheckout(form) && !isDepositSelected()) {
        gateCheckoutForConfigurationChange();
        schedulePreparation();
      }
      return;
    }

    if (!pinPreparedPayload()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      gateCheckoutForConfigurationChange();
      schedulePreparation();
      return;
    }

    /*
     * Allow Shopify's official control to continue natively. Its product
     * datasource reads the pinned live id, quantity, property inputs, and the
     * matching shipping metadata from this dedicated form. It therefore opens
     * a one-product wallet/checkout without consuming the existing Ajax cart.
     */
  }, true);

  const preparationCanRun = () => navigator.onLine !== false && !document.hidden;

  const suspendPreparation = (form = bridge.form) => {
    if (bridge.preparationSuspended || !form) return;
    invalidateIsolatedCheckout();
    stopPendingRequest();
    clearPreparedPayload(form);
    bridge.preparationSuspended = true;
    setFormState(form, 'preparing');
  };

  const handlePreparationEnvironmentChange = () => {
    if (!bridge.apoActive) return;
    if (!preparationCanRun()) {
      if (busyExpressState(bridge.form?.dataset.joaExpressState) ||
        (bridge.form?.dataset.joaExpressState === 'unavailable' && bridge.preparationRecoveryAllowed)) {
        suspendPreparation();
      }
      return;
    }
    if (bridge.preparationSuspended ||
      (bridge.priceQuote && Date.now() - bridge.priceQuote.verifiedAt >= PRICE_QUOTE_MAX_AGE)) {
      schedulePreparation();
    } else scheduleControlSync(0);
  };

  const handleExpressPageShow = (event) => {
    if (!event.persisted) {
      if (bridge.apoActive) schedulePreparation();
      else scheduleControlSync(0);
      return;
    }
    invalidateIsolatedCheckout();
    stopPendingRequest();
    const form = neutralizeExpressForm();
    clearPreparedPayload(form);
    bridge.preparationSuspended = false;
    if (form) {
      form.dataset.joaWalletState = 'resolving';
      setFormState(form, 'preparing');
    }
    if (bridge.apoActive) schedulePreparation();
    else scheduleControlSync(0);
  };

  document.addEventListener('visibilitychange', handlePreparationEnvironmentChange);
  window.addEventListener('online', handlePreparationEnvironmentChange);
  window.addEventListener('offline', handlePreparationEnvironmentChange);
  window.addEventListener('pageshow', handleExpressPageShow);

  scheduleControlSync(0);
})();







